/*
 * Klaviyo Back-in-Stock, client-side subscribe.
 *
 * Replaces the DA Restock app form. Submits to the Klaviyo Client API
 * (public company id, safe for client-side). One delegated submit
 * handler covers every [data-klaviyo-bis] instance on the page (builder
 * modal + standard PDP), so forms injected/shown later still work.
 *
 * Company id is exposed by the theme as window.AG_KLAVIYO_COMPANY_ID.
 * Each form carries the target Shopify variant id in data-variant-id;
 * callers (PDP variant change, builder modal) keep it current.
 */
(function () {
  var COMPANY_ID = window.AG_KLAVIYO_COMPANY_ID || '';
  var MARKETING_LIST_ID = window.AG_KLAVIYO_MARKETING_LIST_ID || '';
  var REVISION = '2024-10-15';
  var ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  var SUBSCRIBE_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  var EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

  function setMsg(el, text) {
    if (!el) return;
    if (text) el.textContent = text;
    el.hidden = false;
  }
  function hide(el) { if (el) el.hidden = true; }

  function subscribe(variantId, email) {
    var payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: { data: { type: 'profile', attributes: { email: email } } }
        },
        relationships: {
          variant: {
            data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId }
          }
        }
      }
    };
    return fetch(ENDPOINT + '?company_id=' + encodeURIComponent(COMPANY_ID), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: REVISION },
      body: JSON.stringify(payload)
    });
  }

  // Opt-in marketing subscription, only fired when the consent box is
  // ticked AND a marketing list id is configured. Best-effort: a failure
  // here never blocks the back-in-stock success.
  function subscribeMarketing(email) {
    if (!MARKETING_LIST_ID) return Promise.resolve();
    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email
                /* Do NOT add a `subscriptions` field here, the 2024-10-15
                   /client/subscriptions/ schema rejects it under profile and
                   400s SILENTLY (same bug that hit the quiz + trade-up). The
                   list relationship below IS the opt-in. */
              }
            }
          }
        },
        relationships: { list: { data: { type: 'list', id: MARKETING_LIST_ID } } }
      }
    };
    return fetch(SUBSCRIBE_ENDPOINT + '?company_id=' + encodeURIComponent(COMPANY_ID), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: REVISION },
      body: JSON.stringify(payload)
    }).then(function (r) {
      /* Observability: a 4xx here used to be fully silent (fetch resolves for
         4xx, and there was no .ok check), surface it so a schema break is
         visible instead of quietly dropping marketing opt-ins. */
      if (r && !r.ok) { try { console.warn('[klaviyo-bis] marketing list subscribe failed:', r.status); } catch (e) {} }
    }).catch(function () { /* best-effort, never blocks BIS success */ });
  }

  /*
   * Standard-PDP reactivity. The theme fires `on:variant:change` on the
   * `.js-product` element with evt.detail.variant ({id, available, ...}).
   * Show the BIS form only when the selected variant is unavailable, and
   * keep its variant id current.
   */
  // Keep the add-to-cart button consistent with the BIS form: when the
  // selected variant is unavailable, show the out-of-stock label and
  // disable it; restore using the button's own data-add-to-cart-text when
  // it's back. Driven off the same `available` signal as the form so the
  // two never disagree (the theme's own update doesn't always fire here).
  function syncAtc(productEl, available) {
    var btn = productEl.querySelector('.add-to-cart');
    if (!btn) return; // sold-out-branch button is already disabled + labelled
    var oosLabel = (window.theme && theme.strings && theme.strings.noStock) || 'Out of stock';
    if (!available) {
      btn.textContent = oosLabel;
      btn.setAttribute('disabled', 'disabled');
    } else {
      if (btn.dataset.addToCartText) btn.textContent = btn.dataset.addToCartText;
      btn.removeAttribute('disabled');
    }
  }

  function initPdpBis() {
    document.querySelectorAll('[data-pdp-bis]').forEach(function (wrap) {
      var productEl = wrap.closest('.js-product');
      if (!productEl) return;
      productEl.addEventListener('on:variant:change', function (evt) {
        var v = evt.detail && evt.detail.variant;
        var bis = wrap.querySelector('[data-klaviyo-bis]');
        if (!v) { wrap.hidden = true; return; }
        if (bis) {
          bis.setAttribute('data-variant-id', v.id);
          var form = bis.querySelector('[data-klaviyo-bis-form]');
          var okEl = bis.querySelector('[data-klaviyo-bis-success]');
          var errEl = bis.querySelector('[data-klaviyo-bis-error]');
          if (form) form.hidden = false;
          if (okEl) okEl.hidden = true;
          if (errEl) errEl.hidden = true;
        }
        wrap.hidden = !!v.available;
        syncAtc(productEl, !!v.available);
      });
      // Initial pass, the form's render-time hidden state reflects the
      // current variant's availability (hidden = available).
      syncAtc(productEl, wrap.hidden);
    });
  }
  if (document.readyState !== 'loading') initPdpBis();
  else document.addEventListener('DOMContentLoaded', initPdpBis);

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-klaviyo-bis-form]');
    if (!form) return;
    e.preventDefault();

    var wrap = form.closest('[data-klaviyo-bis]');
    if (!wrap) return;
    var emailEl = form.querySelector('[data-klaviyo-bis-email]');
    var btn = form.querySelector('[data-klaviyo-bis-submit]');
    var marketingEl = form.querySelector('[data-klaviyo-bis-marketing]');
    var okEl = wrap.querySelector('[data-klaviyo-bis-success]');
    var errEl = wrap.querySelector('[data-klaviyo-bis-error]');
    var variantId = (wrap.getAttribute('data-variant-id') || '').trim();
    var email = (emailEl && emailEl.value || '').trim();
    var wantsMarketing = !!(marketingEl && marketingEl.checked);

    hide(okEl);
    hide(errEl);

    if (!EMAIL_RE.test(email)) {
      setMsg(errEl, 'Please enter a valid email address.');
      if (emailEl) emailEl.focus();
      return;
    }
    if (!variantId) { setMsg(errEl, 'Something went wrong, please try again.'); return; }
    if (!COMPANY_ID) { setMsg(errEl, 'Notifications are temporarily unavailable.'); return; }

    var originalLabel = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }

    subscribe(variantId, email)
      .then(function (res) {
        // Client API returns 202 Accepted; treat any 2xx as success
        // (already-subscribed is idempotent and also returns 2xx).
        if (res.status === 202 || res.ok) {
          if (wantsMarketing) subscribeMarketing(email);
          form.hidden = true;
          setMsg(okEl);
        } else {
          throw new Error('HTTP ' + res.status);
        }
      })
      .catch(function () {
        setMsg(errEl, 'Something went wrong, please try again.');
      })
      .then(function () {
        if (btn) { btn.disabled = false; btn.textContent = originalLabel; }
      });
  });
})();
